import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"); // viewer's local time
const SCOPES = [
  { key: "readonly", label: "Read-only credential", sub: "Service Desk · lookup_order, search, fetch" },
  { key: "filing", label: "E-mail filing credential (write)", sub: "Service Desk · add_order_email + lookups" },
  { key: "full", label: "Shared ChatGPT connector URL", sub: "your own ChatGPT" },
];
const norm = (g) => ({ enabled: !!g?.enabled, scopes: [...(g?.scopes || [])].sort(), allow_bodies: !!g?.allow_bodies });
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const onOff = (v, t) => (v ? t("ON") : t("OFF"));

function SavedState({ saved, t }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="sdc-email-reads-saved">
      <div><div className="text-slate-500">{t("Status previews & history")}</div><span data-testid="sdc-saved-enabled">{onOff(saved.enabled, t)}</span></div>
      <div><div className="text-slate-500">{t("Credential scopes")}</div><span data-testid="sdc-saved-scopes">{saved.scopes.length ? saved.scopes.join(", ") : t("none")}</span></div>
      <div><div className="text-slate-500">{t("Historical e-mail text")}</div><span data-testid="sdc-saved-bodies">{onOff(saved.allow_bodies, t)}</span></div>
      <div><div className="text-slate-500">{t("Last change")}</div><span data-testid="sdc-saved-updated">{fmt(saved.updated_at)}{saved.updated_by ? ` · ${saved.updated_by}` : ""}</span></div>
    </div>
  );
}

function ConfirmDialog({ open, onOpenChange, saved, draft, onConfirm, pending, t }) {
  const row = (label, a, b, id) => (
    <div className="flex justify-between gap-3 text-sm" data-testid={`sdc-confirm-${id}`}>
      <span className="text-slate-600">{label}</span>
      <span className={a === b ? "text-slate-500" : "font-semibold text-slate-900"}>{a} → {b}</span>
    </div>
  );
  const sc = (s) => (s.length ? s.join(", ") : t("none"));
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[calc(100vw-1.5rem)] sm:w-full rounded-lg" data-testid="sdc-email-reads-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("Apply SDC e-mail reading changes?")}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 pt-1">
              {row(t("Status previews & history"), onOff(saved.enabled, t), onOff(draft.enabled, t), "enabled")}
              {row(t("Credential scopes"), sc(saved.scopes), sc(draft.scopes), "scopes")}
              {row(t("Historical e-mail text"), onOff(saved.allow_bodies, t), onOff(draft.allow_bodies, t), "bodies")}
              <p className="text-xs text-slate-500 pt-1">{t("Applies across every order in the CRM for the selected credential scope(s). No credential is created, rotated or revoked; no e-mail is sent.")}</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="sdc-email-reads-cancel">{t("Cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={pending} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="sdc-email-reads-apply">
            {pending ? t("Saving…") : t("Save & apply")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Super-Admin gate for the two SDC read tools (preview_status_email / get_order_emails). Opening changes nothing. */
export function SdcEmailReadsCard() {
  const { t } = useT();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: st, isLoading, isError, error } = useQuery({ queryKey: ["mcp-connector"], queryFn: async () => (await api.get("/mcp-connector/status")).data, refetchInterval: 30_000 });
  const saved = useMemo(() => (st?.email_reads ? { ...norm(st.email_reads), updated_at: st.email_reads.updated_at, updated_by: st.email_reads.updated_by, tools: st.email_reads.tools || [] } : null), [st]);
  const [draft, setDraft] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  const savedKey = saved ? JSON.stringify(norm(saved)) : "";
  const dirty = !!(draft && saved && !same(norm(draft), norm(saved)));
  useEffect(() => { if (saved && !dirty) setDraft(norm(saved)); }, [savedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: (body) => api.put("/mcp-connector/email-reads", body),
    onSuccess: (r) => {
      const v = r?.data?.email_reads;
      setConfirm(false); setFailure(null);
      if (!v) { setResult(null); setFailure(t("Saved, but the server did not return the stored state — reload to verify.")); return; }
      setResult({ ...norm(v), updated_at: v.updated_at, updated_by: v.updated_by });
      setDraft(norm(v));
      qc.setQueryData(["mcp-connector"], r.data);
      toast.success(t("SDC e-mail reading saved"));
    },
    onError: (e) => { setConfirm(false); setResult(null); setFailure(e?.response?.data?.detail || e?.message || t("Save failed")); },
  });
  if (user?.role !== "super_admin") return <div className="p-6 text-sm text-slate-500" data-testid="sdc-email-reads-sa-only">{t("Only a Super Admin can view or change SDC e-mail reading.")}</div>;
  if (isLoading) return <div className="p-6 text-sm text-slate-500 animate-pulse" data-testid="sdc-email-reads-loading">{t("Loading saved state…")}</div>;
  if (isError || !saved) return <div className="p-6 text-sm text-red-700" data-testid="sdc-email-reads-load-error">{t("Could not load the saved state")}{error?.response?.data?.detail ? ` — ${error.response.data.detail}` : ""}. {t("Nothing was changed.")}</div>;
  const d = draft || norm(saved);
  const edit = (next) => { setDraft(next); setResult(null); };
  const toggleScope = (k, on) => edit({ ...d, scopes: on ? [...new Set([...d.scopes, k])].sort() : d.scopes.filter((s) => s !== k) });
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="sdc-email-reads-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-2xl">📖</span>
        <h2 className="font-semibold text-lg">{t("SDC e-mail reading")} <span className="text-slate-400 font-normal text-sm">· {t("Service Desk")}</span></h2>
        <Badge className={`border ${saved.enabled && saved.scopes.length ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}`} data-testid="sdc-email-reads-badge">
          {saved.enabled && saved.scopes.length ? t("ON") : t("OFF")}
        </Badge>
      </div>
      <p className="text-sm text-slate-600">{t("Lets the Service Desk read — never send — two things through the MCP tools preview_status_email and get_order_emails: the exact status e-mail the CRM would send for an order, and the list of e-mails already sent for an order. Access applies across all orders to every credential of the selected scope(s). Off by default; opening this page changes nothing; tokens are never shown here.")}</p>
      <SavedState saved={saved} t={t} />
      <div className="space-y-3" data-testid="sdc-email-reads-controls">
        <label className="flex items-start gap-3 cursor-pointer">
          <Switch checked={d.enabled} onCheckedChange={(v) => edit({ ...d, enabled: !!v })} data-testid="sdc-toggle-enabled" />
          <span className="text-sm"><span className="font-medium">{t("Status previews & e-mail history")}</span><br /><span className="text-xs text-slate-500">{t("preview_status_email (rendered subject/body of a new, unsent status e-mail) and get_order_emails (summaries: recipient, subject, sent time, status option, IDs).")}</span></span>
        </label>
        <div className="pl-12 space-y-2" data-testid="sdc-scope-list">
          <div className="text-xs text-slate-500">{t("Credential scope(s) that receive the tools")}</div>
          {SCOPES.map((s) => {
            const cred = st?.[s.key];
            const state = s.key === "full" ? (st?.enabled ? t("active") : t("off")) : cred?.active ? t("active") : t("none");
            return (
              <label key={s.key} className="flex items-start gap-2 cursor-pointer" data-testid={`sdc-scope-${s.key}`}>
                <Checkbox checked={d.scopes.includes(s.key)} onCheckedChange={(v) => toggleScope(s.key, !!v)} data-testid={`sdc-scope-${s.key}-checkbox`} />
                <span className="text-sm"><span className="font-medium">{t(s.label)}</span> <span className="text-xs text-slate-400">({state})</span><br /><span className="text-xs text-slate-500">{t(s.sub)}</span></span>
              </label>
            );
          })}
          {d.enabled && !d.scopes.length && <div className="text-xs text-amber-700" data-testid="sdc-no-scope-hint">{t("Enabled without a scope: the tools stay hidden from every credential.")}</div>}
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <Switch checked={d.allow_bodies} onCheckedChange={(v) => edit({ ...d, allow_bodies: !!v })} data-testid="sdc-toggle-bodies" />
          <span className="text-sm"><span className="font-medium">{t("Historical e-mail text")}</span><br /><span className="text-xs text-slate-500">{t("Also returns the plain-text body of previously sent e-mails when SDC asks for it (never the HTML, never tracking links). Requires the switch above.")}</span></span>
        </label>
      </div>
      {failure && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="sdc-email-reads-error">{t("Not saved")}: {failure}</div>}
      {result && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="sdc-email-reads-result">
          ✓ {t("Verified from server")}: {t("Status previews & history")} <b>{onOff(result.enabled, t)}</b> · {t("Credential scopes")} <b>{result.scopes.length ? result.scopes.join(", ") : t("none")}</b> · {t("Historical e-mail text")} <b>{onOff(result.allow_bodies, t)}</b> · {fmt(result.updated_at)}{result.updated_by ? ` · ${result.updated_by}` : ""}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={!dirty || save.isPending} className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => setConfirm(true)} data-testid="sdc-email-reads-save-btn">{t("Save changes…")}</Button>
        <Button size="sm" variant="outline" disabled={!dirty || save.isPending} onClick={() => { setDraft(norm(saved)); setFailure(null); }} data-testid="sdc-email-reads-discard-btn">{t("Discard")}</Button>
        {!dirty && <span className="text-xs text-slate-400" data-testid="sdc-email-reads-clean">{t("No unsaved changes")}</span>}
      </div>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} saved={norm(saved)} draft={d} pending={save.isPending} t={t} onConfirm={() => save.mutate(d)} />
    </div>
  );
}
